import React from 'react';
import cx from 'classnames';
import { useSelector, useDispatch } from 'react-redux';
import { makeStyles, useScrollTrigger } from '@material-ui/core';

import { useIntl } from 'ha/i18n';

import { useLocation } from 'ha/helpers/router';
import { useMediaQuery } from 'ha/helpers/MediaQuery';
import units from 'ha/theme/units';

import Grid from 'ha/ui/next/Grid';
import Fader from 'ha/ui/next/Fader';
import Slide from 'ha/ui/next/Slide';
import { getIsAuthenticated } from 'ha/modules/AuthLogic/selectors';
import MobileFiltersModal from 'ha/components/Filters/MobileFiltersModal';
import FilterPopoverContext from 'ha/components/Filters/FilterPopoverContext';

import SearchMeta from '../containers/SearchMeta';
import SuggestionBar from '../containers/SuggestionBar';
import SearchMap from '../containers/SearchMap';
import SearchPageCrawl from '../containers/SearchPageCrawl';
import SearchResults from '../containers/SearchResults';
import SearchResultsLoader from '../containers/SearchResultsLoader';
import SearchOverview from '../containers/SearchOverview';
import MobileMap from '../containers/MobileMap';

import MobileFilters from '../containers/SearchFilters/MobileFilters';
import MobileSearchAlertModal from './SearchAlerts/MobileSearchAlertModal';

import {
  isSuggestionBarVisible as isSuggestionBarVisibleSelector,
  getPreviewNumberOfListings,
} from '../selectors';
import { SuggestionBarActions } from '../actions';
import { filterUpdateButtonText, DefaultTextType } from '../utils';

const useStyles = makeStyles(theme => ({
  wrapper: {
    [theme.breakpoints.up('lg')]: {
      display: 'flex',
      flexDirection: 'column',
      height: 'calc(100vh - 64px)',
      background: 'white',
    },
  },
  limitHeight: {
    height: '100%',
  },
  results: {
    position: 'relative',
    borderRight: units.border(1, 'solid', theme.palette.grey[300]),
    transition: 'padding-bottom 0.3s ease-out',
  },
  listingsContainer: {
    flex: 1,
    maxWidth: '100vw',
    [theme.breakpoints.up('lg')]: {
      maxWidth: '1015px',
      overflowX: 'hidden',
      overflowY: 'auto',
    },
  },
  mapContainer: {
    flex: 1,
    position: 'relative',
  },
  suggestionBar: {
    position: 'sticky',
    width: '100%',
    bottom: 0,
    zIndex: 3,
    fallbacks: {
      position: 'fixed',
    },
  },
}));

const SCROLL_THRESHOLD = 800;

export const SearchLayout: React.FC = () => {
  const classes = useStyles();
  const { T } = useIntl();
  const { lg } = useMediaQuery();
  const {
    query: { page },
  } = useLocation();

  const [
    layoutElement,
    setLayoutElement,
  ] = React.useState<HTMLDivElement | null>(null);

  const dispatch = useDispatch();
  const isAuthenticated = useSelector(getIsAuthenticated);
  const isSuggestionBarVisible = useSelector(isSuggestionBarVisibleSelector);

  const showSuggestionBar = isSuggestionBarVisible && !isAuthenticated;
  const scrollListenerEnabled = !isSuggestionBarVisible && !isAuthenticated;

  const [
    scrollElement,
    setScrollElement,
  ] = React.useState<HTMLDivElement | null>(null);

  const scrollTriggered = useScrollTrigger({
    threshold: SCROLL_THRESHOLD,
    target: lg && scrollElement ? scrollElement : undefined,
  });

  React.useEffect(() => {
    if (!scrollListenerEnabled || !scrollTriggered) {
      return;
    }

    dispatch(SuggestionBarActions.show());
  }, [scrollTriggered, scrollListenerEnabled]);

  // scroll to top on page change for desktop
  React.useEffect(() => {
    if (!scrollElement || !lg) {
      return;
    }

    scrollElement.scrollTop = 0;
  }, [page]);

  const previewNumberOfListings: number = useSelector(
    getPreviewNumberOfListings,
  );

  return (
    <div className={classes.wrapper}>
      <SearchMeta page={page} />
      <SearchPageCrawl page={page} />

      <FilterPopoverContext.Provider value={layoutElement}>
        <Grid
          container
          className={cx(classes.limitHeight)}
          ref={setLayoutElement}
        >
          <div
            className={cx(classes.listingsContainer, classes.limitHeight)}
            ref={setScrollElement}
          >
            <Grid
              container
              direction="column"
              wrap="nowrap"
              className={classes.results}
            >
              <SearchResultsLoader />
              <SearchOverview />

              {!lg ? (
                <React.Fragment>
                  <MobileFiltersModal
                    priceFilterTitle={T('Budget')}
                    filtersComponent={MobileFilters}
                    updateButtonText={filterUpdateButtonText(
                      T,
                      DefaultTextType.ShowResults,
                      previewNumberOfListings,
                    )}
                  />
                  <MobileSearchAlertModal />
                </React.Fragment>
              ) : null}
              <Fader>
                <SearchResults />
              </Fader>
            </Grid>
          </div>
          {lg ? (
            <div className={classes.mapContainer}>
              <SearchMap />
            </div>
          ) : (
            <MobileMap />
          )}
        </Grid>
      </FilterPopoverContext.Provider>

      <Slide direction="up" mountOnEnter unmountOnExit in={showSuggestionBar}>
        <div className={classes.suggestionBar}>
          <SuggestionBar />
        </div>
      </Slide>
    </div>
  );
};

export default SearchLayout;
